import { useRef, useState, useCallback } from 'react';

type Options = {
  speed?: number;
  enableWheel?: boolean;
};

export const useDragScroll = <T extends HTMLElement>({
  speed = 1.5,
  enableWheel = true
}: Options = {}) => {
  const ref = useRef<T | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const isDraggingRef = useRef(false);
  const hasMovedRef = useRef(false);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (!ref.current) return;

    setIsDragging(true);
    isDraggingRef.current = true;
    hasMovedRef.current = false;

    setStartX(e.pageX - ref.current.offsetLeft);
    setScrollLeft(ref.current.scrollLeft);
  }, []);

  const onMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const onMouseUp = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;

    setTimeout(() => {
      hasMovedRef.current = false;
    }, 0);
  }, []);

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !ref.current) return;

      const x = e.pageX - ref.current.offsetLeft;
      const walk = (x - startX) * speed;

      if (Math.abs(walk) > 5) {
        hasMovedRef.current = true;
      }

      e.preventDefault();
      ref.current.scrollLeft = scrollLeft - walk;
    },
    [isDragging, startX, scrollLeft, speed]
  );

  return {
    ref,
    isDraggingRef,
    hasMovedRef,
    bind: {
      onMouseDown,
      onMouseLeave,
      onMouseUp,
      onMouseMove
    }
  };
};
